import React from "react";
import Highcharts from "../../config/highcharts-config";
import HighchartsReact from "highcharts-react-official";
import { useAppSelector } from "@store/index";
import { lineChartConfigSelector } from "@store/slices/line-chart-config-slice";
import { useLineChartOptions } from "@hooks/use-line-chart-options";
import { Box, Typography } from "@mui/material";
import { NoDataIcon } from "@/assets/images";
import { getPeriodTypeLabelForTimeUnit } from "@/utils/rolling-average-utils";

const ConfigurableLineChart: React.FC = () => {
  const { currentConfig } = useAppSelector(lineChartConfigSelector);

  // Use the line chart options hook
  const options = useLineChartOptions({
    config: currentConfig,
    isConfigPreview: true, // This is the config preview component
  });

  const allLinesHidden =
    currentConfig?.series &&
    currentConfig.series.length > 0 &&
    currentConfig.series.every(
      (s) => currentConfig.lineVisibility[s.id] === false,
    );

  const hasNoData =
    !currentConfig?.series ||
    currentConfig.series.length === 0 ||
    currentConfig.series.every((s) => !s.data || s.data.length === 0);

  const isBreakoutActive = !!currentConfig?.breakout?.enabled;

  if (hasNoData || allLinesHidden) {
    return (
      <div className="w-full h-full bg-white flex flex-col items-center justify-center">
        <NoDataIcon
          style={{
            width: "110px",
            height: "110px",
            color: "var(--color-hp-bg-disabled)",
          }}
        />
        <p className="text-sm font-medium">
          {allLinesHidden && isBreakoutActive
            ? "All breakout lines are hidden. Show at least one line to display data."
            : "No data available."}
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ width: "100%", height: "100%" }}>
        <HighchartsReact
          highcharts={Highcharts}
          options={options}
          containerProps={{ style: { width: "100%", height: "100%" } }}
          label="Configurable Line Chart"
        />
      </div>
      {currentConfig?.statisticalTesting?.enabled &&
        (() => {
          const hasSignificantChanges = currentConfig.series?.some((series) =>
            series.data?.some(
              (point: any) =>
                point.statSignificance !== 0 &&
                point.statSignificance !== undefined,
            ),
          );
          const significanceLevel =
            currentConfig.statisticalTesting.significanceLevel || "90%";
          const isRollingAverage =
            currentConfig?.rollingAverage?.enabled &&
            currentConfig.rollingAverage.windowSize;
          const windowSize = currentConfig?.rollingAverage?.windowSize;

          // Get the period type label from the timeUnit
          const periodType = currentConfig?.timeUnit
            ? getPeriodTypeLabelForTimeUnit(currentConfig.timeUnit)
            : "period";

          const comparisonLabel = isRollingAverage
            ? `Previous Rolling Window`
            : `Previous Period`;
          const comparisonDescription = isRollingAverage
            ? `previous ${windowSize}-${periodType} rolling average`
            : `previous period`;

          if (hasSignificantChanges) {
            return (
              <Box
                sx={{
                  mt: 1,
                  p: 1,
                  px: 2.5,
                  backgroundColor: "#f5f5f5",
                  borderRadius: 1,
                  border: "1px solid #e0e0e0",
                  width: "100%",
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {currentConfig.statisticalTesting?.markerStyle === "circles" ? (
                    <>
                      <span className="text-[#10B981]">●</span> /{" "}
                      <span className="text-[#EF4444]">○</span>
                    </>
                  ) : (
                    <>
                      <span className="text-[#10B981]">▲</span> /{" "}
                      <span className="text-[#EF4444]">▼</span>
                    </>
                  )}{" "}
                  {comparisonLabel}: Significant increase/decrease compared to{" "}
                  {comparisonDescription} at {significanceLevel} level of
                  significance.
                </Typography>
              </Box>
            );
          } else {
            return (
              <Box
                sx={{
                  mt: 1,
                  p: 1,
                  px: 2.5,
                  backgroundColor: "#f5f5f5",
                  borderRadius: 1,
                  border: "1px solid #e0e0e0",
                  width: "100%",
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  No statistically significant differences found compared to{" "}
                  {comparisonDescription} at {significanceLevel} level of
                  significance.
                </Typography>
              </Box>
            );
          }
        })()}
    </div>
  );
};

export default ConfigurableLineChart;
